import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, theme, message, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { router } from '@/router';
import { useAuthStore } from '@/store/useAuthStore';
import { useEffect, useState } from 'react';
import { STORAGE_KEYS } from '@/config/constants';

// 创建 QueryClient 实例
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 分钟
    },
    mutations: {
      onError: (error: Error) => {
        message.error(error.message || '操作失败');
      },
    },
  },
});

// ✅ 新增：为审计日志查询配置单独的 staleTime（不缓存）
queryClient.setQueryDefaults(['audit-logs'], {
  staleTime: 0, // 审计日志需要实时数据，不缓存
});

// 全局错误处理组件
function GlobalErrorHandler() {
  const error = useAuthStore((state) => state.error);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  return null;
}

// ✅ 新增：应用初始化组件，负责恢复登录状态
function AppInitializer({ children }: { children: React.ReactNode }) {
  // ✅ 修复：只解构需要的 fetchProfile，移除未使用的 token
  const { fetchProfile } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      // 检查 localStorage 中是否有 token
      const storedToken = localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
      
      if (storedToken) {
        // 如果有 token，尝试获取用户信息
        try {
          await fetchProfile();
        } catch (err) {
          console.error('Failed to restore session:', err);
          // 如果获取失败（如 token 过期），logout 会清理状态
        }
      }
      
      setIsInitialized(true);
    };

    init();
  }, [fetchProfile]);

  if (!isInitialized) {
    // 初始化期间显示加载状态，防止闪烁
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        locale={zhCN}
        theme={{
          algorithm: theme.defaultAlgorithm,
          token: {
            colorPrimary: '#1890ff',
            borderRadius: 6,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          },
          components: {
            Table: {
              headerBg: '#fafafa',
              headerColor: '#333',
              headerSortHoverBg: '#f0f0f0',
            },
            Button: {
              fontWeight: 500,
            },
          },
        }}
      >
        <GlobalErrorHandler />
        {/* ✅ 包裹初始化逻辑 */}
        <AppInitializer>
          <RouterProvider router={router} />
        </AppInitializer>
      </ConfigProvider>
    </QueryClientProvider>
  );
}